import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import { ModalState } from '@/components/header/types';
import { ModalLayout } from '../ModalLayout';
import { useInjection } from 'inversify-react';
import { type IFrameController } from '@/engine/app/IFrame.controller';
import { TYPES } from '@/engine/ioc';
import { getOperationModeTimeInHours } from '@/engine/app/MipEnvValue';
import styled from 'styled-components';

export interface ModalProps {
  modalStateChangeHandler: (modal: ModalState) => void;
  isVisible: boolean;
}

export interface DialogTitleProps {
  id: string;
  children?: ReactNode;
  onClose: () => void;
}

export const SessionTimeModal: FC<ModalProps & { setSessionTimeButtonDisabled: Dispatch<SetStateAction<boolean>> }> = ({
  setSessionTimeButtonDisabled,
  ...props
}) => {
  const iframeController = useInjection<IFrameController>(TYPES.IFrameController);
  const operationTimeInHours = getOperationModeTimeInHours();

  return (
    <ModalLayout title="Session time" {...props}>
      <DialogContent dividers>
        <JustifiedP>
          For security reasons, you will be automatically logged out from the application after 15 minutes of
          inactivity. This session time can be extended to <strong>{operationTimeInHours} hours</strong> by pressing
          `Extend`. To keep the default session time of 15 minutes and return to the plan, click `Cancel`.
        </JustifiedP>
        <br />
        <JustifiedP>
          <i>
            Extending your session implies that any unauthorized personnel can access your plan for up to{' '}
            {operationTimeInHours} hours should you leave your computer unattended. To ensure no unauthorized personnel
            can access your case, log-out from the case management system.
          </i>
        </JustifiedP>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'right',
          gap: '1rem',
          alignItems: 'center',
          paddingTop: '0.75rem',
          paddingRight: '0.75rem'
        }}
      >
        <Button
          onClick={(): void => {
            props.modalStateChangeHandler(ModalState.None);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={(): void => {
            setSessionTimeButtonDisabled(true);
            iframeController.mipService.startOperationMode(() => setSessionTimeButtonDisabled(false));
            props.modalStateChangeHandler(ModalState.None);
          }}
        >
          Extend
        </Button>
      </DialogActions>
    </ModalLayout>
  );
};

const JustifiedP = styled.p`
  text-align: justify;
`;
